import {
  IContract,
  TInstallmentStatus,
} from 'modules/customer/domain/entities';

type TVectisInstallmentStatus =
  | 'ABERTA'
  | 'QUITADA'
  | 'ATRASADA'
  | 'VENCIDO'
  | 'REJEITADA'
  | 'PROGRAMADO'
  | 'CANCELADA';

export interface IVectisContract {
  id: number;
  idCliente: number;
  nomeCliente: string;
  idUnidade: number;
  idEmpresa: number;
  codigo: number;
  descricaoItensContrato: string[];
  situacaoContrato: 'NORMAL' | 'QUITADO' | 'PARCELA_VENCIDA';
  valorTotal: number;
  dataProximaParcela: string;
  dataCompra: string;
  formaCompra: string;
  urlPdfContrato: string;
  parcelas?: Array<{
    id: number;
    numeroParcela: number;
    dataCobrancaOriginal: string;
    dataCobranca: string;
    valorParcela: number;
    valorFinalParcela: number;
    valorMulta: number;
    situacaoParcela: TVectisInstallmentStatus;
    dadosPagamento: Array<{ finalCartao: string }>;
  }>;
}

const CONTRACT_STATUS_DICTIONARY: Record<
  IVectisContract['situacaoContrato'],
  IContract['status']
> = {
  NORMAL: 'PENDING',
  PARCELA_VENCIDA: 'OVERDUE',
  QUITADO: 'PAID',
};

const INSTALLMENT_STATUS_DICTIONARY: Record<
  TVectisInstallmentStatus,
  TInstallmentStatus
> = {
  ABERTA: 'PENDING',
  ATRASADA: 'OVERDUE',
  VENCIDO: 'OVERDUE',
  REJEITADA: 'OVERDUE',
  QUITADA: 'PAID',
  PROGRAMADO: 'PENDING',
  CANCELADA: 'CANCELLED',
};

export class Contract implements IContract {
  public id: IContract['id'];
  public code: IContract['code'];
  public customer: IContract['customer'];
  public unit: IContract['unit'];
  public enterprise: IContract['enterprise'];
  public url: IContract['url'];
  public areas: IContract['areas'];
  public status: IContract['status'];
  public total: IContract['total'];
  public nextInstallment: IContract['nextInstallment'];
  public purchase: IContract['purchase'];
  public installments: IContract['installments'];

  constructor({
    id,
    idCliente,
    nomeCliente,
    codigo,
    idUnidade,
    idEmpresa,
    descricaoItensContrato,
    situacaoContrato,
    valorTotal,
    dataProximaParcela,
    dataCompra,
    formaCompra,
    urlPdfContrato,
    parcelas,
  }: IVectisContract) {
    this.id = id;
    this.code = codigo;
    this.customer = { id: idCliente, name: nomeCliente };
    this.unit = { id: idUnidade };
    this.enterprise = { id: idEmpresa };
    this.url = urlPdfContrato;
    this.areas = descricaoItensContrato;
    this.status = CONTRACT_STATUS_DICTIONARY[situacaoContrato];
    this.total = valorTotal;
    this.nextInstallment = dataProximaParcela;

    const { installmentPlan, paymentMethod } = this.getPurchase(formaCompra);
    this.purchase = {
      date: dataCompra,
      installmentPlan,
      paymentMethod,
    };
    this.installments = parcelas
      ? this.buildInstallments(parcelas)
      : this.buildInstallmentsForInstallmentlessContract();
  }

  private buildInstallments(
    parcelas: IVectisContract['parcelas'] = [],
  ): IContract['installments'] {
    return parcelas
      .map(parcela => {
        const hasExclusivePaymentData =
          parcela.dataCobranca !== parcela.dataCobrancaOriginal;

        const isKnownStatus =
          parcela.situacaoParcela in INSTALLMENT_STATUS_DICTIONARY;

        if (!isKnownStatus) return null;

        return {
          id: parcela.id,
          number: parcela.numeroParcela,
          chargeDate: parcela.dataCobranca,
          paymentDate: hasExclusivePaymentData
            ? parcela.dataCobranca
            : undefined,
          value: {
            initial: parcela.valorParcela,
            withInterest: parcela.valorFinalParcela,
          },
          status: INSTALLMENT_STATUS_DICTIONARY[parcela.situacaoParcela],
          cardNumber: parcela.dadosPagamento[0]?.finalCartao,
          hasFineOrInterest:
            parcela.situacaoParcela === 'ATRASADA' &&
            parcela.valorParcela !== parcela.valorFinalParcela,
          fine: {
            value: parcela.valorMulta,
          },
        } as IContract['installments'][number];
      })
      .filter(installment => installment !== null) as IContract['installments'];
  }

  private getPurchase(purchaseMethod: string) {
    const differentPurchaseMethods = purchaseMethod.split(' + ');

    const installmentPlans: string[] = [];
    const methods = new Set<string>();

    for (const purchase of differentPurchaseMethods) {
      const [installmentPlan, paymentMethod] = purchase.split(' - ');

      installmentPlans.push(installmentPlan);
      methods.add(paymentMethod);
    }

    return {
      installmentPlan: installmentPlans.join(' + '),
      paymentMethod: [...methods.values()].join(' + '),
    };
  }

  private buildInstallmentsForInstallmentlessContract(): IContract['installments'] {
    return [
      {
        id: this.id,
        chargeDate: this.purchase.date,
        number: 1,
        status: this.status,
        value: {
          initial: this.total,
          withInterest: this.total,
        },
        fine: { value: 0 },
        hasFineOrInterest: false,
      },
    ];
  }
}
